import React, { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useElementHeight } from '../../../../../hooks/use-element-height';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { CollapsableSection } from '../../../../skyview/right-menu/components/collapsable-section';
import { Stack } from '../../../../stack/stack';
import { TextBox } from '../../../../text-box/text-box';
import { StickySearchBox } from './types';
import { VolumeWithAsset } from './utils';
import { VolumeListVolumeCard } from './volume-list-volume-card';

const toggleSelectedVolume = (
  volumes: VolumeWithAsset[],
  selectedVolume: VolumeWithAsset,
  callback: (volumes: VolumeWithAsset[]) => void,
) => {
  const updatedVolumes = volumes.map((volume: VolumeWithAsset) =>
    volume.id === selectedVolume.id ? { ...volume, selected: !selectedVolume.selected } : volume,
  );
  callback(updatedVolumes);
};

function searchFilter(x: VolumeWithAsset, nameFilter: string) {
  if (!nameFilter) {
    return true;
  }

  return (
    x.volume.name?.toLowerCase().includes(nameFilter) ||
    x.asset.name.toLowerCase().includes(nameFilter) ||
    x.sourceName?.toLowerCase().includes(nameFilter)
  );
}

type Props = {
  volumesWithAssets: VolumeWithAsset[];
  setVolumesWithAssets: (items: VolumeWithAsset[]) => void;
  loading: boolean;
};

export const VolumeMultiSelectListSection = (props: Props) => {
  const { t } = useTranslation();
  const [volumeSectionGroupExpanded, setVolumeSectionGroupExpanded] = React.useState(true);
  const [searchText, setSearchText] = React.useState('');
  const headerRef = useRef(null);
  const headerHeight = useElementHeight(headerRef);

  return (
    <CollapsableSection
      expanded={volumeSectionGroupExpanded}
      headerRef={headerRef}
      title={t('volumeListSection.header', { ns: 'skyviewReport' })}
      titleUpperCase={true}
      tooltip={{
        text: (
          <Trans
            components={{ br: <br /> }}
            i18nKey="volumeListSection.tooltip"
            ns="skyviewReport"
          />
        ),
        title: t('volumeListSection.header', { ns: 'skyviewReport' }),
      }}
      onSetExpanded={() => {
        setVolumeSectionGroupExpanded((expanded) => !expanded);
      }}
    >
      <StickySearchBox height={headerHeight}>
        <LabelledContainer text={t('searchHeader', { ns: 'skyviewReport' })}>
          <TextBox value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </LabelledContainer>
      </StickySearchBox>

      <Stack direction="column" spacing={0.8}>
        {props.volumesWithAssets
          ?.filter((x) => searchFilter(x, searchText.toLowerCase()))
          ?.map((volume: VolumeWithAsset) => {
            return (
              <VolumeListVolumeCard
                key={volume.id}
                loading={props.loading}
                selected={volume.selected}
                volume={volume}
                onClick={() => {
                  if (!props.loading) {
                    toggleSelectedVolume(
                      props.volumesWithAssets,
                      volume,
                      props.setVolumesWithAssets,
                    );
                  }
                }}
              />
            );
          })}
      </Stack>
    </CollapsableSection>
  );
};
